import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AsYouType, parsePhoneNumber } from 'libphonenumber-js/min';
import { cn } from '@/lib/utils';
import { buildPhoneCountries } from './phoneCountries';
import { languageToBcp47 } from './phoneLocales';
import { buildTriggerAriaLabel, getPhoneUiStrings } from './phoneUiStrings';
import { CountrySelect } from './CountrySelect';
import './IntlPhoneField.css';

function formatNationalDigits(digits, country) {
  const ayt = new AsYouType(country);
  ayt.reset();
  for (const ch of digits) {
    ayt.input(ch);
  }
  return { chars: ayt.getChars(), e164: ayt.getNumberValue() || '' };
}

/**
 * Campo telefone estilo intl-tel-input: bandeira + DDI + número (E.164 no form).
 */
export function IntlPhoneField({
  id,
  value,
  onChange,
  defaultCountry,
  language,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  className,
  hasError
}) {
  const bcp47 = useMemo(() => languageToBcp47(language), [language]);
  const countries = useMemo(() => buildPhoneCountries(bcp47), [bcp47]);
  const ui = useMemo(() => getPhoneUiStrings(language), [language]);

  const [country, setCountry] = useState(defaultCountry);
  const [countryOpen, setCountryOpen] = useState(false);
  const [display, setDisplay] = useState('');

  const countryRef = useRef(defaultCountry);
  const skipNextExternalSync = useRef(false);

  useEffect(() => {
    countryRef.current = country;
  }, [country]);

  const selectedRow = useMemo(() => {
    return (
      countries.find((c) => c.iso === country) ||
      countries.find((c) => c.iso === defaultCountry) ||
      countries[0]
    );
  }, [countries, country, defaultCountry]);

  const syncFromE164 = useCallback((e164) => {
    if (!e164) {
      setDisplay('');
      return;
    }
    try {
      const p = parsePhoneNumber(e164);
      if (p) {
        setCountry(p.country);
        countryRef.current = p.country;
        const national = String(p.nationalNumber);
        const { chars } = formatNationalDigits(national, p.country);
        setDisplay(chars);
      }
    } catch {
      setDisplay(String(e164));
    }
  }, []);

  useEffect(() => {
    if (skipNextExternalSync.current) {
      skipNextExternalSync.current = false;
      return;
    }
    syncFromE164(value || '');
  }, [value, syncFromE164]);

  const emitChange = useCallback(
    (nextE164) => {
      skipNextExternalSync.current = true;
      onChange(nextE164);
    },
    [onChange]
  );

  const handleInputChange = (e) => {
    const raw = e.target.value;
    const cc = countryRef.current;

    if (raw.trim().startsWith('+')) {
      try {
        const p = parsePhoneNumber(raw);
        if (p) {
          setCountry(p.country);
          countryRef.current = p.country;
          const national = String(p.nationalNumber);
          const { chars, e164 } = formatNationalDigits(national, p.country);
          setDisplay(chars);
          emitChange(e164);
          return;
        }
      } catch {
        /* continua como entrada nacional */
      }
    }

    const { chars, e164 } = formatNationalDigits(raw, cc);
    setDisplay(chars);
    emitChange(e164);
  };

  const handleSelectCountry = (iso) => {
    countryRef.current = iso;
    setCountry(iso);
    setCountryOpen(false);

    const current = value;
    if (!current) {
      setDisplay('');
      emitChange('');
      return;
    }
    try {
      const p = parsePhoneNumber(current);
      if (p) {
        const national = String(p.nationalNumber);
        const { chars, e164 } = formatNationalDigits(national, iso);
        setDisplay(chars);
        emitChange(e164);
      }
    } catch {
      setDisplay('');
      emitChange('');
    }
  };

  const triggerAriaLabel = buildTriggerAriaLabel(ui, selectedRow);

  return (
    <div
      className={cn(
        'intl-phone-field',
        hasError && 'intl-phone-field--error',
        className
      )}
    >
      <div className="intl-phone-shell">
        <CountrySelect
          countries={countries}
          selectedRow={selectedRow}
          open={countryOpen}
          onOpenChange={setCountryOpen}
          onSelectCountry={handleSelectCountry}
          searchPlaceholder={ui.searchPlaceholder}
          emptyText={ui.empty}
          triggerAriaLabel={triggerAriaLabel}
          hasError={hasError}
        />
        <input
          type="tel"
          id={id}
          inputMode="tel"
          autoComplete="tel-national"
          className="intl-phone-input"
          value={display}
          onChange={handleInputChange}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
        />
      </div>
    </div>
  );
}
